import React, { useState, useMemo } from 'react';
import Dinero from 'dinero.js';
import tableStyles from '../../atoms/rich-text/rich-text.module.scss';
import { IPricingBlockData } from '../../../models/components/pricing-block-component-model';
import { replaceAttrSpaceWithHyphen } from '../../../utils/string-helpers/replace-attr-space-with-hyphen';
import ToggleButtonGroup from '../toggle-button-group/toggle-button-group';
import { IToggleButton } from '../../../models/toggle-button/IToggleButton';

type Props = {
  title: string;
  copyText?: string;
  studyLevel: string;
  costs: IPricingBlockData[];
  academicYearLabel: string;
  tableHeaderCostsLabel: string;
  tableHeaderApproxAmountLabel: string;
  tableHeaderEstimatedTotalLabel: string;
  themeColor: string;
};

const formatDinero = (amount: number, currency: string): string => {
  const formatted = Dinero({
    amount: Math.floor(amount),
    currency,
    precision: 0,
  }).toFormat();

  return formatted.slice(0, -3); // To remove decimals
};

export default function PricingBlock({
  title,
  copyText,
  studyLevel,
  costs,
  academicYearLabel,
  tableHeaderCostsLabel,
  tableHeaderApproxAmountLabel,
  tableHeaderEstimatedTotalLabel,
  themeColor,
}: Props) {
  const academicYears = costs.filter((e) => e.costs.length > 0).map((e) => e.academicYear);
  const [selectedYear, setSelectedYear] = useState(academicYears[0]);

  const selectedYearCosts = costs.find((e) => e.academicYear === selectedYear);

  // It wont come to this, but just in case
  if (!selectedYearCosts || !selectedYearCosts.costs.length) {
    return null;
  }

  const currencyCode = selectedYearCosts.costs[0].currencyCode;
  const minTotal = selectedYearCosts.costs.reduce((sum, c) => sum + c.min, 0);
  const maxTotal = selectedYearCosts.costs.reduce((sum, c) => sum + c.max, 0);
  const pricingBlockId = `${replaceAttrSpaceWithHyphen(studyLevel)}-pricing-block`;
  const pricingBlockTableId = `${replaceAttrSpaceWithHyphen(studyLevel)}-pricing-block-table`;

  const changeAcademicYear = (year: string) => {
    setSelectedYear(academicYearsMap[year]);
  };
  const academicYearsMap: { [key: string]: string } = {};

  const buttonProps: IToggleButton[] = academicYears.map((year: string) => {
    academicYearsMap[`${year}-${studyLevel}`] = year;
    return {
      label: year,
      value: `${year}-${studyLevel}`,
      isChecked: selectedYear === year,
      group: `acYearGroup-${studyLevel}`,
      handleOnToggle: changeAcademicYear,
      inputColor: themeColor,
    };
  });

  return (
    <div id={pricingBlockId}>
      <h2>{title}</h2>
      {copyText && <p>{copyText}</p>}
      <p className='mb-0'>
        {academicYears.length > 1 && <strong>{academicYearLabel}</strong>}
        {academicYears.length === 1 && (
          <>
            {academicYearLabel}: <strong>{selectedYear}</strong>
          </>
        )}
      </p>
      <div className='mt-2 mb-3'>
        {academicYears.length > 1 && <ToggleButtonGroup toggleButtonProps={buttonProps} />}
      </div>

      <div
        className={tableStyles['rich-text-table-block']}
        id={pricingBlockTableId}
        data-study-level={replaceAttrSpaceWithHyphen(studyLevel)}
        data-academic-year={selectedYear}
      >
        <table className={tableStyles['rich-text-table']}>
          <tbody>
            <tr>
              <th>
                <p>{tableHeaderCostsLabel}</p>
              </th>
              <th>
                <p>
                  {tableHeaderApproxAmountLabel} ({currencyCode})
                </p>
              </th>
            </tr>

            {selectedYearCosts.costs.map((cost) => (
              <tr
                key={`${replaceAttrSpaceWithHyphen(cost.displayName)}-${replaceAttrSpaceWithHyphen(studyLevel)}`}
              >
                <td data-title={tableHeaderCostsLabel}>
                  <p>{cost.displayName}</p>
                </td>
                <td data-title={`${tableHeaderApproxAmountLabel} (${currencyCode})`}>
                  <p>
                    {`${formatDinero(cost.min, cost.currencyCode)} - ${formatDinero(
                      cost.max,
                      cost.currencyCode,
                    )}`}
                  </p>
                </td>
              </tr>
            ))}

            {selectedYearCosts.costs.length > 1 && (
              <tr>
                <td data-title={tableHeaderCostsLabel}>
                  <p>
                    <strong>{tableHeaderEstimatedTotalLabel}</strong>
                  </p>
                </td>
                <td data-title={`${tableHeaderApproxAmountLabel} (${currencyCode})`}>
                  <p>
                    <strong>
                      {`${formatDinero(minTotal, currencyCode)} - ${formatDinero(
                        maxTotal,
                        currencyCode,
                      )}`}
                    </strong>
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
